import {
  type Color,
  FLAG_ACTION,
  FLAG_DROP,
  type Game,
  type GameStatus,
  KING,
  WHITE,
  makePiece,
  moveFlags,
  moveFrom,
  moveTo,
  moveToUci,
  other,
  reasonText,
} from '@chessbut/engine';
import type { ClockState } from '@chessbut/shared';
import { ArrowUpDown, Award, BookOpen, ChevronLeft, EllipsisVertical, Flame, Settings as SettingsIcon } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { navigate } from '../lib/router.tsx';
import { useSettings } from '../lib/settings.ts';
import { type SoundName, sounds } from '../lib/sound.ts';
import { load, save } from '../lib/storage.ts';
import { openSettings } from '../lib/ui.ts';
import { pieceImage, variantUi } from '../variants/ui.ts';
import { Board } from './Board.tsx';
import { DayBadge, variantStyle } from './Brand.tsx';
import { Modal } from './Modal.tsx';
import { MoveList } from './MoveList.tsx';
import { PlayerBar } from './PlayerBar.tsx';
import { RulesModal } from './Rules.tsx';
import { SideBoard } from './SideBoard.tsx';

const NO_MOVES: number[] = [];

export interface PlayerLabel {
  name: string;
  username?: string | null;
  rating?: string | null;
  isYou?: boolean;
  offline?: boolean;
  streak?: number;
  /** A rated computer opponent. */
  bot?: boolean;
}

export interface EndInfo {
  /** Drives the verdict sticker: "You won" / "You lost" / "Draw"… */
  verdict: 'won' | 'lost' | 'drew' | 'aborted' | 'white' | 'black';
  subtitle?: string;
  gains?: { points?: number | null; rating?: { diff: number; after: number } | null; streak?: number | null };
  /** Share text preview (the Wordle-style block). */
  share?: string;
  extra?: ReactNode;
  actions: ReactNode;
}

export interface GameViewProps {
  game: Game;
  /** Changes whenever `game` was mutated. */
  version: number;
  white: PlayerLabel;
  black: PlayerLabel;
  /** Colour at the bottom by default. */
  perspective: Color;
  /** The viewer's colour for win/lose sounds and the twist bar (null: spectator or pass & play). */
  you: Color | null;
  movable: (color: Color) => boolean;
  status: GameStatus;
  clock?: { state: ClockState; at: number } | null;
  thinking?: string | null;
  onMove(uci: string): void;
  /** Chip in the top bar, e.g. "rated · 5+3", "vs computer". */
  mode: string;
  /** Where the back button goes. */
  backTo?: string;
  notices?: ReactNode;
  /** Up to four buttons for the action row. */
  actions: ReactNode;
  end: EndInfo | null;
}

export function GameView(props: GameViewProps) {
  const { game, version, status } = props;
  const variant = game.variant;
  const ui = variantUi(variant.id);
  const settings = useSettings();
  const [flipped, setFlipped] = useState(false);
  const [viewPly, setViewPly] = useState<number | null>(null);
  const [showEnd, setShowEnd] = useState(true);
  const [menu, setMenu] = useState(false);
  // First time someone plays a variant, explain the twist before they move.
  const [rulesOpen, setRulesOpen] = useState(() =>
    props.you !== null || props.movable(0) || props.movable(1) ? !load<boolean>(`seen-rules:${variant.id}`, false) : false,
  );
  const closeRules = () => {
    save(`seen-rules:${variant.id}`, true);
    setRulesOpen(false);
  };
  const draggedPly = useRef(-1);
  const lastSoundPly = useRef(game.ply);

  const ply = viewPly ?? game.ply;
  const position = game.positionAt(ply);
  const start = game.positionAt(0);
  const live = viewPly === null;
  const rules = game.rules;

  // Secret games (hidden pieces) in pass & play: each player looks only when it's their turn,
  // behind a "pass the device" screen. `seat` is whoever last looked.
  const secretPass = !!ui.secret && props.you === null;
  const [seat, setSeat] = useState<Color | null>(null);
  // The curtain stays up (even over the history) until whoever's turn it is says it's them.
  const curtain = secretPass && !status.over && seat !== game.turn;
  const viewer: Color | null = status.over ? null : (props.you ?? (secretPass ? seat : variant.hiddenInfo ? position.turn : null));
  const base = secretPass ? (seat ?? WHITE) : props.perspective;
  const orientation = flipped ? other(base) : base;
  const shown = useMemo(() => {
    if (!ui.view) return position;
    // Behind the curtain nobody's pieces show, not even to a screen reader.
    if (curtain) return ui.view(ui.view(position, WHITE), other(WHITE));
    return ui.view(position, viewer);
  }, [ui, position, viewer, curtain]);

  useEffect(() => setViewPly(null), [version]);

  useEffect(() => {
    if (status.over) setShowEnd(true);
  }, [status.over]);

  // Sounds and a tiny haptic tick for new moves; a jingle when the game ends.
  const wasOver = useRef(status.over);
  useEffect(() => {
    if (status.over && !wasOver.current) {
      wasOver.current = true;
      if (props.you === null || status.winner === undefined) sounds.win();
      else (status.winner === props.you ? sounds.win : sounds.lose)();
      lastSoundPly.current = game.ply;
      return;
    }
    wasOver.current = status.over;
    if (game.ply === lastSoundPly.current) return;
    const forward = game.ply > lastSoundPly.current;
    lastSoundPly.current = game.ply;
    if (!forward) return;
    const rec = game.history.at(-1);
    if (!rec) return;
    const pos = game.position;
    if (!variant.hiddenInfo && rules.inCheck(pos)) sounds.check();
    else if (!variant.hiddenInfo && rules.isThreatened(pos)) sounds.danger();
    else if (rec.captured) sounds.capture();
    else sounds.move();
    const extra = new Set<SoundName>();
    for (const e of rec.events) {
      const s = ui.eventSound?.(e);
      if (s) extra.add(s);
    }
    for (const s of extra) sounds[s]();
    navigator.vibrate?.(10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, status.over]);

  const legal = live && !status.over && !curtain && props.movable(position.turn) ? game.legalMoves() : NO_MOVES;
  // Actions that aren't made on the main board go to buttons and the side board.
  // Pieces to put back from a hand (Wild Card's +2): pick which kind, then tap a square.
  const dropTypes = useMemo(() => {
    const types = new Set<number>();
    for (const m of legal) if (moveFlags(m) & FLAG_DROP && !rules.defs[moveFrom(m)]?.neutral) types.add(moveFrom(m));
    return [...types];
  }, [legal, rules]);
  const [dropPick, setDropPick] = useState<number | null>(null);
  const dropType = dropPick !== null && dropTypes.includes(dropPick) ? dropPick : (dropTypes[0] ?? null);
  const boardLegal = useMemo(() => {
    let out = legal;
    if (out.some((m) => moveFlags(m) & FLAG_ACTION)) out = out.filter((m) => !(moveFlags(m) & FLAG_ACTION) || ui.boardAction?.(m));
    if (dropTypes.length > 1) out = out.filter((m) => !(moveFlags(m) & FLAG_DROP) || moveFrom(m) === dropType);
    return out;
  }, [legal, ui, dropTypes, dropType]);
  const side = ui.sideBoard?.(position) ?? null;
  const buttons = ui.actions?.(position, legal) ?? [];
  // Hidden kings mustn't light up.
  const check = live && !variant.hiddenInfo && rules.inCheck(position);
  const danger = live && !variant.hiddenInfo && !check && rules.isThreatened(position);
  const rec = game.history[ply - 1];
  const lastMove = useMemo(() => {
    if (!rec) return null;
    if (moveFlags(rec.move) & FLAG_ACTION && !ui.boardAction?.(rec.move)) return null;
    // A secret move (a setup swap) doesn't light up its squares either.
    if (ui.hideInMoveList?.(rec)) return null;
    return moveFlags(rec.move) & FLAG_DROP ? { from: moveTo(rec.move), to: moveTo(rec.move) } : { from: moveFrom(rec.move), to: moveTo(rec.move) };
  }, [rec, ui]);
  const decorations = useMemo(() => ui.decorations?.(shown) ?? [], [ui, shown]);

  const onMove = (uci: string, dragged: boolean) => {
    if (dragged) draggedPly.current = game.ply + 1;
    props.onMove(uci);
  };

  const clockFor = (color: Color) => {
    if (!props.clock) return null;
    const { state, at } = props.clock;
    return { ms: color === WHITE ? state.white : state.black, running: state.running === color, at };
  };

  const label = (c: Color) => (c === WHITE ? props.white : props.black);
  const bar = (c: Color) => (
    <PlayerBar
      color={c}
      name={label(c).name}
      username={label(c).username}
      rating={label(c).rating}
      isYou={label(c).isYou}
      bot={label(c).bot}
      offline={label(c).offline}
      streak={label(c).streak}
      active={!status.over && position.turn === c}
      position={position}
      start={start}
      hideMaterial={!!ui.hideMaterial && !status.over}
      note={ui.twist ? null : (ui.playerNote?.(shown, c) ?? null)}
      clock={clockFor(c)}
    />
  );

  // The bar under the board: game state first, then the variant's twist, then whose move it is.
  const toMove = label(position.turn);
  let barText: string;
  let barTone: 'alert' | 'twist' | 'plain' = 'plain';
  let pips: { on: number; total: number; title?: string } | undefined;
  const twist = live && !status.over ? ui.twist?.(shown, props.you ?? (secretPass ? seat : null)) : null;
  if (status.over) barText = reasonText(variant, status.reason);
  else if (check) {
    barText = toMove.isYou ? 'Check! Protect your king.' : `Check! ${toMove.name} must respond.`;
    barTone = 'alert';
  } else if (danger) {
    barText = ui.threatText?.(!!toMove.isYou, toMove.name) ?? 'Danger! Respond to the threat.';
    barTone = 'alert';
  } else if (props.thinking) barText = props.thinking;
  else if (twist) {
    barText = twist.text;
    barTone = twist.alert ? 'alert' : 'twist';
    pips = twist.pips;
  } else barText = toMove.isYou ? 'Your move' : `${toMove.name} to move`;

  return (
    <div className={`game-screen ${side ? 'has-side' : ''}`} style={variantStyle(variant)}>
      <header className="game-topbar">
        <button className="btn ghost icon-only" aria-label="Back" onClick={() => navigate(props.backTo ?? `/${variant.id}`)}>
          <ChevronLeft className="icon" />
        </button>
        <div className="game-title">
          <DayBadge variant={variant} />
          <span className="game-title-text">
            <span className="variant-name">{variant.info.name}</span>
            <span className="game-mode">{props.mode}</span>
          </span>
        </div>
        <button className="btn ghost icon-only" aria-label="Menu" onClick={() => setMenu(true)}>
          <EllipsisVertical className="icon" />
        </button>
      </header>

      <div className="game-layout">
        <div className="board-column">
          {bar(other(orientation))}
          <div className="board-wrap">
          <div className="board-slot" inert={curtain} aria-hidden={curtain || undefined}>
          <Board
            position={shown}
            orientation={orientation}
            ui={ui}
            legalMoves={boardLegal}
            events={rec?.events ?? null}
            animKey={ply}
            dragged={draggedPly.current === ply}
            lastMove={lastMove}
            checkSquare={check ? position.royals[position.turn] : -1}
            dangerSquare={danger ? position.royals[position.turn] : -1}
            decorations={decorations}
            showLegalMoves={settings.showLegalMoves}
            showHints={settings.showHints}
            coordinates={settings.coordinates}
            onMove={onMove}
          />
          </div>
          {curtain && (
            <div className="curtain" role="dialog" aria-label="Pass the device">
              <p>
                Pass to <b>{label(game.turn).name}</b>
              </p>
              <span className="muted small">No peeking at their pieces.</span>
              <button className="btn strong" autoFocus onClick={() => setSeat(game.turn)}>
                I’m {label(game.turn).name}: show my pieces
              </button>
            </div>
          )}
          </div>
          {bar(orientation)}
          <div className={`twist-bar ${barTone === 'alert' ? 'is-alert' : barTone === 'plain' ? 'is-plain' : ''}`} key={barTone === 'alert' ? `alert-${ply}` : 'bar'}>
            <span className="twist-art" aria-hidden>
              {props.thinking && !status.over ? (
                <span className="spinner" />
              ) : barTone === 'plain' ? (
                <img src={pieceImage(makePiece(position.turn, KING))} alt="" />
              ) : ui.heroImage ? (
                <img src={ui.heroImage} alt="" />
              ) : (
                variant.info.emoji
              )}
            </span>
            <span className="twist-text">{barText}</span>
            {pips && (
              <span className="count" title={pips.title}>
                <span className="pips">
                  {Array.from({ length: pips.total }, (_, i) => (
                    <i key={i} className={i < pips.on ? 'on' : ''} />
                  ))}
                </span>
              </span>
            )}
          </div>
          {dropTypes.length > 1 && (
            <div className="variant-actions drop-pick" role="radiogroup" aria-label="Piece to put back">
              {dropTypes.map((t) => (
                <button
                  key={t}
                  role="radio"
                  aria-checked={t === dropType}
                  aria-label={rules.defs[t]?.name}
                  className={`btn ${t === dropType ? 'strong' : ''}`}
                  onClick={() => setDropPick(t)}
                >
                  <img src={pieceImage(makePiece(position.turn, t))} alt="" />
                </button>
              ))}
            </div>
          )}
          {buttons.length > 0 && (
            <div className="variant-actions">
              {buttons.map((b) => (
                <button key={b.move} className={`btn ${b.tone ?? ''}`} title={b.title} onClick={() => props.onMove(moveToUci(b.move))}>
                  {b.label}
                </button>
              ))}
            </div>
          )}
          {!live && (
            <button className="viewing-history" onClick={() => setViewPly(null)}>
              Viewing move {ply} of {game.ply}. Back to live
            </button>
          )}
          {props.notices}
          <div className="game-actions">
            {props.actions}
            {status.over && !showEnd && props.end && (
              <button className="btn" onClick={() => setShowEnd(true)}>
                <Award className="icon" />
                Result
              </button>
            )}
          </div>
        </div>
        <aside className="side-panel">
          {side && (
            <SideBoard spec={side} orientation={orientation} legalMoves={legal} showLegalMoves={settings.showLegalMoves} onMove={(u) => props.onMove(u)} />
          )}
          <MoveList history={game.history} ui={ui} viewPly={viewPly} onSelect={setViewPly} />
        </aside>
      </div>

      {status.over && showEnd && props.end && <ResultSheet end={props.end} status={status} variantName={variant.info.name} onClose={() => setShowEnd(false)} />}
      {rulesOpen && <RulesModal variant={variant} onClose={closeRules} />}
      {menu && (
        <Modal title="Game menu" onClose={() => setMenu(false)}>
          <div className="menu-list">
            <button
              className="btn block"
              onClick={() => {
                setMenu(false);
                setRulesOpen(true);
              }}
            >
              <BookOpen className="icon" /> Rules
            </button>
            <button
              className="btn block"
              onClick={() => {
                setFlipped((f) => !f);
                setMenu(false);
              }}
            >
              <ArrowUpDown className="icon" /> Flip board
            </button>
            <button
              className="btn block"
              onClick={() => {
                setMenu(false);
                openSettings();
              }}
            >
              <SettingsIcon className="icon" /> Settings
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ResultSheet({ end, status, variantName, onClose }: { end: EndInfo; status: GameStatus; variantName: string; onClose(): void }) {
  const word = { won: 'won', lost: 'lost', drew: 'drew', aborted: 'aborted', white: 'wins', black: 'wins' }[end.verdict];
  const who = end.verdict === 'white' ? 'White' : end.verdict === 'black' ? 'Black' : end.verdict === 'aborted' ? 'Game' : 'You';
  const g = end.gains;
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet result" role="dialog" aria-label="Result" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-grip" aria-hidden />
        <h2 className="verdict">
          {who} <span className={`verdict-word ${end.verdict}`}>{word}</span>
        </h2>
        <p className="verdict-sub">{end.subtitle ?? `${reasonText(null, status.reason)} · ${variantName}`}</p>
        {g && (g.points != null || g.rating || g.streak) ? (
          <div className="gains">
            {g.points != null && (
              <div className="gain is-points">
                <b className="num">+{g.points}</b>
                <span>points</span>
              </div>
            )}
            {g.rating && (
              <div className={`gain ${g.rating.diff >= 0 ? 'up' : 'down'}`}>
                <b className="num">
                  {g.rating.diff >= 0 ? '+' : ''}
                  {g.rating.diff}
                </b>
                <span>rating → {g.rating.after}</span>
              </div>
            )}
            {g.streak ? (
              <div className="gain is-streak">
                <b className="num">
                  <Flame className="icon sm" aria-hidden />
                  {g.streak}
                </b>
                <span>day streak</span>
              </div>
            ) : null}
          </div>
        ) : null}
        {end.extra}
        {end.share && <pre className="share-preview">{end.share}</pre>}
        <div className="sheet-actions">
          {end.actions}
          <button className="btn ghost block quiet" onClick={onClose}>
            View board
          </button>
        </div>
      </div>
    </div>
  );
}

/** Verdict for the result sheet from the viewer's point of view. */
export function verdictFor(status: GameStatus, you: Color | null): EndInfo['verdict'] {
  if (status.reason === 'aborted') return 'aborted';
  if (status.winner === undefined) return 'drew';
  if (you === null) return status.winner === WHITE ? 'white' : 'black';
  return status.winner === you ? 'won' : 'lost';
}

/** "You won!", "Black wins", "Draw"… */
export function endTitle(status: GameStatus, you: Color | null): string {
  if (!status.over) return '';
  if (status.reason === 'aborted') return 'Game aborted';
  if (status.winner === undefined) return 'Draw';
  if (you === null) return `${status.winner === WHITE ? 'White' : 'Black'} wins`;
  return status.winner === you ? 'You won!' : 'You lost';
}
